import { useEffect, useState } from "react";
import { useClientProjectsStore } from "../../../store/useClientProjectsStore";
import {
  MIcon,
  ModalShell,
  HealthChip,
  EmptyState,
  InvoiceChip,
  CertaintyBadge,
  attentionIcon,
  fmtShort,
  money,
  actionIcon,
  PRIORITY_TONE,
} from "./shared";

/**
 * The detailed portfolio view behind "View More".
 *
 * Only the two narrative paragraphs come from the model. Every milestone,
 * action, invoice, date and percentage below is computed by the API from
 * project records, so nothing here can be invented.
 *
 * Two distinctions the UI has to preserve, because the client acts on them:
 *
 *   confirmed vs potential   Something that has already happened is shown in
 *                            error tone and stated. Something that might is
 *                            shown in warning tone and labelled a risk.
 *   attention vs blocking    An invoice that wants paying is not the same as
 *                            one the project cannot proceed past.
 *
 * Every row carries the record it came from, so the client is never left with
 * a sentence about a project and no way to open it.
 */

const Section = ({
  icon,
  title,
  meta,
  children,
}: {
  icon: string;
  title: string;
  meta?: string;
  children: React.ReactNode;
}) => (
  <section className="mb-lg last:mb-0">
    <div className="flex items-center gap-2 mb-sm">
      <MIcon name={icon} className="text-primary text-[18px]" />
      <h3 className="text-[12px] font-bold uppercase tracking-wider text-on-surface">{title}</h3>
      {meta && <span className="text-[11px] text-on-surface-variant font-medium">· {meta}</span>}
    </div>
    {children}
  </section>
);

/** A row that opens the project its fact came from, or a plain row if it has none. */
const LinkedRow = ({
  ref: target,
  onOpenProject,
  className = "",
  children,
}: {
  ref?: { projectId?: string | null } | null;
  onOpenProject: (id: string) => void;
  className?: string;
  children: React.ReactNode;
}) => {
  const id = target?.projectId;
  if (!id) return <div className={className}>{children}</div>;
  return (
    <button type="button" onClick={() => onOpenProject(id)} className={`${className} text-left w-full`}>
      {children}
    </button>
  );
};

const AiProjectSummaryModal = ({
  open,
  onClose,
  onOpenProject,
}: {
  open: boolean;
  onClose: () => void;
  onOpenProject: (id: string) => void;
}) => {
  const fetchAiSummary = useClientProjectsStore((s) => s.fetchAiSummary);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    fetchAiSummary().then((d) => {
      if (!cancelled) {
        setData(d);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const ago = (iso?: string) => {
    if (!iso) return "";
    const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
    return mins < 1 ? "just now" : mins < 60 ? `${mins}m ago` : `${Math.round(mins / 60)}h ago`;
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      icon="auto_awesome"
      title="AI Project Summary"
      subtitle={
        data
          ? `A detailed overview of your current project portfolio · Updated ${ago(data.generatedAt)}`
          : "A detailed overview of your current project portfolio"
      }
      width="max-w-4xl"
    >
      {loading && !data ? (
        <div className="py-xl text-center text-body-sm text-on-surface-variant">
          Reading your project records…
        </div>
      ) : !data ? (
        <EmptyState title="Summary unavailable" detail="Please try again in a moment." />
      ) : (
        <>
          {/* Overall health */}
          <Section icon="monitor_heart" title="Overall Project Health">
            <div className="p-md rounded-xl bg-surface-container-low border border-outline-variant/40">
              {data.overall.activeCount > 0 && (
                <p className="text-body-sm font-bold text-tertiary mb-1">
                  {data.overall.onTrackCount} of {data.overall.activeCount} active{" "}
                  {data.overall.activeCount === 1 ? "project" : "projects"} on track ·{" "}
                  {data.overall.onTrackPercent}%
                </p>
              )}
              <p className="text-body-sm text-on-surface leading-relaxed">{data.overall.narrative}</p>
            </div>
          </Section>

          {/* What actually needs attention, confirmed before potential */}
          {data.attentionItems?.length > 0 && (
            <Section
              icon="priority_high"
              title="Needs Your Attention"
              meta={`${data.attentionItems.filter((i: any) => i.certainty === "confirmed").length} confirmed`}
            >
              <div className="space-y-sm">
                {data.attentionItems.map((item: any, i: number) => (
                  <LinkedRow
                    key={i}
                    ref={item.ref}
                    onOpenProject={onOpenProject}
                    className={`p-md rounded-xl border transition-colors ${
                      item.certainty === "confirmed"
                        ? "border-error/30 bg-error-container/30 hover:border-error/60"
                        : "border-amber-300/50 bg-amber-50/60 hover:border-amber-400"
                    }`}
                  >
                    <div className="flex items-start gap-2.5">
                      <MIcon
                        name={attentionIcon(item.kind)}
                        className={`text-[18px] mt-0.5 ${
                          item.certainty === "confirmed" ? "text-error" : "text-amber-700"
                        }`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <span className="text-body-sm font-bold text-on-surface">{item.title}</span>
                          <CertaintyBadge certainty={item.certainty} />
                        </div>
                        <p className="text-body-sm text-on-surface-variant leading-relaxed">{item.detail}</p>
                      </div>
                    </div>
                  </LinkedRow>
                ))}
              </div>
            </Section>
          )}

          {/* Active projects */}
          {data.activeProjects?.length > 0 && (
            <Section icon="work" title="Active Project Updates">
              <div className="space-y-sm">
                {data.activeProjects.map((p: any) => (
                  <button
                    key={p.id}
                    onClick={() => onOpenProject(p.id)}
                    className="w-full text-left p-md rounded-xl border border-outline-variant hover:border-primary/40 hover:bg-surface-container-low transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
                      <span className="text-body-md font-bold text-on-surface">{p.name}</span>
                      <span className="flex items-center gap-2">
                        <span className="text-body-sm font-bold text-on-surface">{p.progress}%</span>
                        <HealthChip health={p.health} />
                      </span>
                    </div>
                    <p className="text-body-sm text-on-surface-variant leading-relaxed">{p.narrative}</p>
                    <div className="flex items-center gap-x-4 gap-y-1 mt-2 flex-wrap text-[11px] text-on-surface-variant">
                      {p.currentStage && (
                        <span>
                          Current: <b className="text-on-surface">{p.currentStage}</b>
                        </span>
                      )}
                      {p.action ? (
                        <span>
                          Action: <b className="text-error">{p.action}</b>
                        </span>
                      ) : p.nextStage ? (
                        <span>
                          Next: <b className="text-on-surface">{p.nextStage}</b>
                        </span>
                      ) : null}
                      {p.eta && (
                        <span>
                          ETA: <b className="text-on-surface">{fmtShort(p.eta)}</b>
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </Section>
          )}

          {/* Recently completed */}
          {data.recentMilestones?.length > 0 && (
            <Section
              icon="check_circle"
              title="Recently Completed Milestones"
              meta={`${data.recentMilestones.length} completed this week`}
            >
              <ul className="space-y-1.5">
                {data.recentMilestones.map((m: any, i: number) => (
                  <li key={i}>
                    <LinkedRow
                      ref={m.ref}
                      onOpenProject={onOpenProject}
                      className="flex items-start gap-2 text-body-sm rounded-lg px-1 py-0.5 -mx-1 hover:bg-surface-container-low transition-colors"
                    >
                      <MIcon name="check" className="text-tertiary text-[16px] mt-0.5" />
                      <span className="text-on-surface">
                        <b>{m.project}:</b> {m.milestone}
                        <span className="text-on-surface-variant"> · Completed {fmtShort(m.date)}</span>
                      </span>
                    </LinkedRow>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Upcoming milestones */}
          {data.upcomingMilestones?.length > 0 && (
            <Section icon="event_upcoming" title="Upcoming Milestones">
              <ul className="space-y-1.5">
                {data.upcomingMilestones.map((m: any, i: number) => (
                  <li key={i}>
                    <LinkedRow
                      ref={m.ref}
                      onOpenProject={onOpenProject}
                      className="flex items-start gap-2 text-body-sm rounded-lg px-1 py-0.5 -mx-1 hover:bg-surface-container-low transition-colors"
                    >
                      <MIcon name="radio_button_unchecked" className="text-outline text-[16px] mt-0.5" />
                      <span className="text-on-surface">
                        <b>{m.project}:</b> {m.milestone}
                        <span className="text-on-surface-variant"> · {fmtShort(m.date)}</span>
                      </span>
                    </LinkedRow>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Actions */}
          {data.actions?.length > 0 && (
            <Section icon="notification_important" title="Actions Requiring Attention">
              <div className="space-y-sm">
                {data.actions.map((a: any) => {
                  const overdue = !!a.dueDate && new Date(a.dueDate).getTime() < Date.now();
                  return (
                    <LinkedRow
                      key={a.id}
                      ref={a.ref}
                      onOpenProject={onOpenProject}
                      className={`p-md rounded-xl border flex items-start justify-between gap-3 flex-wrap transition-colors ${
                        overdue
                          ? "border-error/30 hover:border-error/60"
                          : "border-outline-variant hover:border-primary/40"
                      }`}
                    >
                      <div className="flex items-start gap-2.5 min-w-0">
                        <MIcon name={actionIcon(a.type)} className="text-primary text-[18px] mt-0.5" />
                        <div className="min-w-0">
                          <p className="text-body-sm font-bold text-on-surface">{a.title}</p>
                          <p className="text-[11px] text-on-surface-variant mt-0.5">
                            {a.projectName}
                            {a.dueDate ? ` · ${overdue ? "Was due" : "Due"} ${fmtShort(a.dueDate)}` : ""}
                          </p>
                        </div>
                      </div>
                      <span
                        className={`px-2.5 py-1 rounded-full text-[11px] font-bold whitespace-nowrap ${
                          PRIORITY_TONE[a.priority] || PRIORITY_TONE.Required
                        }`}
                      >
                        {a.priority}
                      </span>
                    </LinkedRow>
                  );
                })}
              </div>
            </Section>
          )}

          {/* Invoices */}
          {data.invoices?.length > 0 && (
            <Section
              icon="payments"
              title="Invoice Summary"
              meta={`${data.invoices.length} ${
                data.invoices.length === 1 ? "invoice requires" : "invoices require"
              } attention`}
            >
              <div className="space-y-sm">
                {data.invoices.map((i: any, idx: number) => (
                  <LinkedRow
                    key={idx}
                    ref={i.ref}
                    onOpenProject={onOpenProject}
                    className={`p-md rounded-xl border flex items-center justify-between gap-3 flex-wrap transition-colors ${
                      i.blocking
                        ? "border-error/30 bg-error-container/20 hover:border-error/60"
                        : "border-outline-variant hover:border-primary/40"
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="text-body-sm font-bold text-on-surface">
                        {money(i.amount, i.currency)}
                        {i.project ? ` — ${i.project}` : ""}
                      </p>
                      <p className="text-[11px] text-on-surface-variant mt-0.5">
                        {i.number}
                        {i.due ? ` · Due ${fmtShort(i.due)}` : ""}
                        {i.paid > 0 && i.total ? ` · ${money(i.paid, i.currency)} of ${money(i.total, i.currency)} paid` : ""}
                      </p>
                      {/* Only said when the project workflow confirms the dependency. */}
                      {i.blocking && (
                        <p className="text-[11px] font-semibold text-error mt-1">
                          The next stage cannot begin until this is paid.
                        </p>
                      )}
                    </div>
                    <InvoiceChip status={i.status} />
                  </LinkedRow>
                ))}
              </div>
            </Section>
          )}

          {/* Coming up */}
          {data.comingUp?.length > 0 && (
            <Section icon="calendar_month" title="Coming Up">
              <ul className="space-y-1.5">
                {data.comingUp.map((e: any, i: number) => (
                  <li key={i}>
                    <LinkedRow
                      ref={e.ref}
                      onOpenProject={onOpenProject}
                      className="text-body-sm text-on-surface rounded-lg px-1 py-0.5 -mx-1 hover:bg-surface-container-low transition-colors"
                    >
                      <b className="text-on-surface-variant">{fmtShort(e.date)}</b> — {e.label}
                    </LinkedRow>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {!data.activeProjects?.length &&
            !data.attentionItems?.length &&
            !data.recentMilestones?.length &&
            !data.actions?.length &&
            !data.invoices?.length && (
              <EmptyState
                icon="task_alt"
                title="Nothing needs your attention"
                detail="There are no open actions, overdue invoices or upcoming deadlines right now."
              />
            )}
        </>
      )}
    </ModalShell>
  );
};

export default AiProjectSummaryModal;
